import React from 'react';
import { useDarkMode } from '../../context/DarkModeContext';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Here you can log to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { isDarkMode } = this.props;
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div
          className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
            }`}
        >
          <div
            className={`max-w-2xl w-full p-8 rounded-lg shadow-xl ${isDarkMode
                ? 'bg-gray-800 border border-gray-700'
                : 'bg-white border border-gray-200'
              }`}
          >
            <div className="text-center mb-6">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${isDarkMode ? 'bg-red-900/30' : 'bg-red-100'
                  }`}
              >
                <svg
                  className={`w-8 h-8 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1
                className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
              >
                Something went wrong
              </h1>
              <p
                className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}
              >
                We're sorry, but something unexpected happened. Please try reloading the page.
              </p>
            </div>

            {isDevelopment && this.state.error && (
              <div
                className={`mb-6 p-4 rounded-lg overflow-auto max-h-64 ${isDarkMode
                    ? 'bg-gray-900 border border-gray-700'
                    : 'bg-gray-100 border border-gray-200'
                  }`}
              >
                <p
                  className={`text-sm font-mono mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'
                    }`}
                >
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre
                    className={`text-xs font-mono overflow-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${isDarkMode
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to use hooks
const ErrorBoundary = ({ children, fallback }) => {
  const { isDarkMode } = useDarkMode();
  return (
    <ErrorBoundaryClass isDarkMode={isDarkMode} fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  );
};

export default ErrorBoundary;

